import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, MicOff, Pause, Play, Square, Keyboard, Edit3, Check, X, AlertTriangle, Volume2, Loader2, Sparkles, FileText, Stethoscope, ClipboardList, ChevronDown, ChevronUp, Activity, Pill, TestTube, StickyNote, Thermometer, Heart, Settings2 } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Silence detection configuration
const SILENCE_THRESHOLD = 0.015;  // Increased slightly to be more sensitive to silence
const SILENCE_DURATION = 2000;    // Increased to 2 seconds to allow for natural pauses
const MIN_RECORDING_DURATION = 1000;  // Minimum 1 second of audio before processing

// Language - Auto Detect only (supports English, Hindi, Telugu and 90+ languages)
const LANGUAGE_MODE = { code: null, label: 'Auto Detect', hint: 'Supports English, Hindi, Telugu & 90+ languages' };

// OPD Mode configurations
const OPD_MODES = {
  minimal: {
    label: 'Minimal OPD',
    description: 'Fastest - Quick entity extraction',
    soapCollapsed: true,
    diagnosisSingleLine: true,
    entityRealTime: true
  },
  detailed: {
    label: 'Detailed OPD',
    description: 'Full history, vitals, differentials',
    soapCollapsed: false,
    diagnosisSingleLine: false,
    differentialMandatory: true
  }
};

const AIClinicalAssistant = ({ 
  onTranscriptReady, 
  onSOAPGenerated,
  onEntitiesExtracted,
  onDiagnosisSuggested,
  onAssessmentSaved,
  sessionId,
  doctorId,
  babyId,
  patientContext,
  onClose
}) => {
  const [mode, setMode] = useState('idle');
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rawTranscript, setRawTranscript] = useState('');
  const [editedTranscript, setEditedTranscript] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false); // Show status after recording stops
  const [soapNotes, setSOAPNotes] = useState(null);
  const [extractedEntities, setExtractedEntities] = useState(null);
  const [diagnosisSuggestions, setDiagnosisSuggestions] = useState(null);
  // Changed order: entities first, then soap, then diagnosis
  const [expandedSections, setExpandedSections] = useState({ entities: true, soap: false, diagnosis: false });
  const [selectedLanguage, setSelectedLanguage] = useState(null); // null = auto-detect
  const [opdMode, setOpdMode] = useState('minimal'); // 'minimal' or 'detailed'
  
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const silenceTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  const [audioLevel, setAudioLevel] = useState(0);
  const [isSilent, setIsSilent] = useState(true);

  useEffect(() => {
    return () => stopAllAudio();
  }, []);

  const stopAllAudio = () => {
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    if (audioContextRef.current) audioContextRef.current.close();
  };

  const monitorAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);
    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      const value = (dataArray[i] - 128) / 128;
      sum += value * value;
    }
    const rms = Math.sqrt(sum / dataArray.length);
    setAudioLevel(rms);
    const currentlySilent = rms < SILENCE_THRESHOLD;
    setIsSilent(currentlySilent);
    
    if (mode === 'ai-listening' && isRecording && !isPaused) {
      if (currentlySilent) {
        if (!silenceTimerRef.current) {
          silenceTimerRef.current = setTimeout(() => {
            const recordingDuration = Date.now() - recordingStartTimeRef.current;
            if (recordingDuration >= MIN_RECORDING_DURATION && audioChunksRef.current.length > 0) {
              processAudioChunk();
            }
          }, SILENCE_DURATION);
        }
      } else {
        if (silenceTimerRef.current) {
          clearTimeout(silenceTimerRef.current);
          silenceTimerRef.current = null;
        }
      }
    }
    animationFrameRef.current = requestAnimationFrame(monitorAudioLevel);
  }, [mode, isRecording, isPaused]);

  const startRecording = async (selectedMode) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000  // 16kHz is good for speech recognition
        } 
      });
      streamRef.current = stream;
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);
      
      // Try opus codec first, fallback to default
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          console.log('Audio chunk received:', event.data.size, 'bytes, total chunks:', audioChunksRef.current.length);
        }
      };
      
      mediaRecorder.onstop = async () => {
        console.log('MediaRecorder stopped, chunks:', audioChunksRef.current.length);
        if (audioChunksRef.current.length > 0) {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
          console.log('Final audio blob size:', audioBlob.size, 'bytes');
          if (audioBlob.size > 5000) {  // Only transcribe if we have meaningful audio
            await transcribeAudio(audioBlob);
          }
          audioChunksRef.current = [];
        }
      };
      
      // Collect data every 500ms for more granular chunks
      mediaRecorder.start(500);
      recordingStartTimeRef.current = Date.now();
      setIsRecording(true);
      setMode(selectedMode);
      monitorAudioLevel();
      toast.success('Consult Assist started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to access microphone');
    }
  };

  const processAudioChunk = async () => {
    if (audioChunksRef.current.length === 0) {
      console.log('No audio chunks to process');
      return;
    }
    
    // Create a copy of the chunks before clearing
    const chunksToProcess = [...audioChunksRef.current];
    audioChunksRef.current = [];
    recordingStartTimeRef.current = Date.now();
    
    const audioBlob = new Blob(chunksToProcess, { type: 'audio/webm' });
    
    // Check if audio blob is large enough (at least 5KB to have meaningful audio)
    if (audioBlob.size < 5000) {
      console.log('Audio blob too small:', audioBlob.size, 'bytes');
      return;
    }
    
    console.log('Processing audio chunk:', audioBlob.size, 'bytes');
    await transcribeAudio(audioBlob);
  };

  const transcribeAudio = async (audioBlob) => {
    try {
      setIsTranscribing(true);  // Show transcribing status
      setIsProcessing(true);
      setProcessingStep('Converting speech to text...');
      
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;
      
      setProcessingStep('Transcribing & translating to English...');
      
      const response = await axios.post(`${API}/api/ai-clinical/transcribe`, {
        audio_base64: audioBase64,
        mime_type: 'audio/webm',
        language: null, // Auto-detect
        translate_to_english: true // Always translate to English for medical documentation
      });
      
      if (response.data.success && response.data.text) {
        const newText = response.data.text;
        setRawTranscript(prev => {
          const updated = prev ? `${prev} ${newText}` : newText;
          setEditedTranscript(updated);
          return updated;
        });
        createAuditLog('transcription', audioBlob.size.toString(), newText);
        toast.success('Transcription complete');
      }
    } catch (error) {
      console.error('Transcription error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Transcription failed: ${errorMsg}`);
    } finally {
      setIsTranscribing(false);
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const stopRecording = () => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    
    // Set transcribing status BEFORE stopping recorder (so UI shows immediately)
    if (audioChunksRef.current.length > 0) {
      setIsTranscribing(true);
      setProcessingStep('Preparing audio for transcription...');
    }
    
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    setIsRecording(false);
    setIsPaused(false);
  };

  const togglePause = () => {
    if (isPaused) {
      mediaRecorderRef.current?.resume();
      monitorAudioLevel();
      setIsPaused(false);
    } else {
      mediaRecorderRef.current?.pause();
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      setIsPaused(true);
    }
  };

  const createAuditLog = async (actionType, inputData, outputData, accepted = null, edited = null) => {
    try {
      await axios.post(`${API}/api/ai-clinical/audit-log`, {
        session_id: sessionId || 'new_session',
        doctor_id: doctorId || 'unknown',
        action_type: actionType,
        input_data: inputData.substring(0, 1000),
        output_data: outputData.substring(0, 1000),
        accepted,
        edited
      });
    } catch (error) {
      console.error('Failed to create audit log:', error);
    }
  };

  const processWithAI = async () => {
    const textToProcess = editedTranscript || rawTranscript;
    if (!textToProcess.trim()) { toast.error('No text to process'); return; }
    setIsProcessing(true);
    try {
      // Step 1: Extract entities FIRST (with medications from database)
      setProcessingStep('Extracting entities...');
      const entitiesResponse = await axios.post(`${API}/api/ai-clinical/extract-entities-enhanced`, { 
        text: textToProcess, 
        context: patientContext,
        opd_mode: opdMode // Pass OPD mode for appropriate extraction depth
      });
      setExtractedEntities(entitiesResponse.data);
      createAuditLog('entity_extraction', textToProcess, JSON.stringify(entitiesResponse.data));
      
      // Step 2: Generate SOAP notes (minimal or detailed based on mode)
      setProcessingStep('Generating SOAP notes...');
      const soapResponse = await axios.post(`${API}/api/ai-clinical/structure-soap-enhanced`, { 
        text: textToProcess, 
        context: patientContext,
        opd_mode: opdMode // Pass OPD mode for minimal vs detailed SOAP
      });
      setSOAPNotes(soapResponse.data);
      createAuditLog('soap_generation', textToProcess, JSON.stringify(soapResponse.data));
      
      // Step 3: Generate diagnosis (primary + differential only, NO red flags display)
      setProcessingStep('Generating diagnosis...');
      const chiefComplaints = entitiesResponse.data.chief_complaints?.join(', ') || textToProcess.substring(0, 200);
      const diagnosisResponse = await axios.post(`${API}/api/ai-clinical/suggest-diagnosis-enhanced`, {
        chief_complaints: chiefComplaints,
        symptoms: entitiesResponse.data.symptoms?.join(', '),
        patient_context: patientContext,
        opd_mode: opdMode // Pass for single-line vs detailed diagnosis
      });
      setDiagnosisSuggestions(diagnosisResponse.data);
      createAuditLog('diagnosis_suggestion', chiefComplaints, JSON.stringify(diagnosisResponse.data));
      
      // Show Entities FIRST (expanded), then SOAP (collapsed for minimal), then Diagnosis
      const isMinimal = opdMode === 'minimal';
      setExpandedSections({ 
        entities: true, 
        soap: !isMinimal, // Collapsed for minimal OPD
        diagnosis: true 
      });
      toast.success('AI processing complete!');
    } catch (error) {
      console.error('AI processing error:', error);
      toast.error('AI processing failed');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // Save assessment to database (linked to session and doctor)
  const saveAssessment = async (medicalSessionId) => {
    if (!medicalSessionId || medicalSessionId === 'new') return null;
    
    try {
      const assessmentData = {
        medical_session_id: medicalSessionId,
        doctor_id: doctorId || 'unknown',
        baby_id: babyId || 'unknown',
        transcript: editedTranscript || rawTranscript,
        soap_notes: soapNotes,
        extracted_entities: extractedEntities,
        diagnosis_suggestions: diagnosisSuggestions
      };
      
      const response = await axios.post(`${API}/api/ai-clinical/assessment`, assessmentData);
      if (response.data.success) {
        toast.success('AI Assessment saved to session');
        if (onAssessmentSaved) onAssessmentSaved(response.data.id);
        return response.data.id;
      }
    } catch (error) {
      console.error('Failed to save assessment:', error);
    }
    return null;
  };

  const acceptSOAPNotes = () => { 
    if (soapNotes && onSOAPGenerated) { 
      onSOAPGenerated(soapNotes); 
      createAuditLog('soap_accepted', JSON.stringify(soapNotes), 'accepted', true, false); 
      toast.success('SOAP notes applied to form'); 
    }
  };
  
  const acceptEntities = () => { 
    if (extractedEntities && onEntitiesExtracted) { 
      onEntitiesExtracted(extractedEntities); 
      createAuditLog('entities_accepted', JSON.stringify(extractedEntities), 'accepted', true, false); 
      
      // Show detailed toast about what was applied
      const applied = [];
      if (extractedEntities.vitals && Object.values(extractedEntities.vitals).some(v => v)) applied.push('Vitals');
      if (extractedEntities.chief_complaints_text) applied.push('Chief Complaints');
      if (extractedEntities.assessment_text) applied.push('Assessment');
      if (extractedEntities.tests_text) applied.push('Tests');
      if (extractedEntities.diagnosis_text) applied.push('Diagnosis');
      if (extractedEntities.medications?.length > 0) applied.push(`${extractedEntities.medications.length} Medication(s)`);
      if (extractedEntities.additional_notes) applied.push('Notes');
      
      toast.success(`Applied: ${applied.join(', ')}`);
    }
  };
  
  const acceptDiagnosis = () => { 
    if (diagnosisSuggestions && onDiagnosisSuggested) { 
      onDiagnosisSuggested(diagnosisSuggestions); 
      createAuditLog('diagnosis_accepted', JSON.stringify(diagnosisSuggestions), 'accepted', true, false); 
      toast.success('Diagnosis applied'); 
    }
  };
  
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));

  // Audio Wave Icon Component
  const WaveIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 12h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M6 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M10 5v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M14 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M18 10v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      <path d="M22 12h-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WaveIcon className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-purple-900">Consult Assist</h3>
          <Badge variant="outline" className="text-xs bg-purple-100 text-purple-700 border-purple-300">Beta</Badge>
        </div>
        <div className="flex items-center gap-2">
          {/* OPD Mode Toggle */}
          <div className="flex items-center gap-1 bg-white rounded-lg border border-purple-200 p-0.5">
            <button
              onClick={() => setOpdMode('minimal')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${opdMode === 'minimal' ? 'bg-purple-600 text-white' : 'text-purple-600 hover:bg-purple-50'}`}
            >
              ⚡ Minimal
            </button>
            <button
              onClick={() => setOpdMode('detailed')}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${opdMode === 'detailed' ? 'bg-purple-600 text-white' : 'text-purple-600 hover:bg-purple-50'}`}
            >
              📋 Detailed
            </button>
          </div>
          {onClose && <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>}
        </div>
      </div>

      {mode === 'idle' && !rawTranscript && (
        <div className="space-y-3">
          {/* OPD Mode Description */}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            <Settings2 className="w-4 h-4" />
            <span>
              <strong>{OPD_MODES[opdMode].label}:</strong> {OPD_MODES[opdMode].description}
            </span>
          </div>
          
          {/* Auto Detect Info */}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
            <span className="text-lg">🌐</span>
            <span><strong>Auto Detect</strong> - Supports English, Hindi, Telugu & 90+ languages</span>
          </div>
          
          {/* Single Consult Assist Button */}
          <Button 
            onClick={() => startRecording('ai-listening')} 
            className="w-full flex items-center justify-center gap-2 h-auto py-4 bg-purple-600 hover:bg-purple-700"
          >
            <WaveIcon className="w-5 h-5" />
            <Mic className="w-5 h-5" />
            <span className="font-medium">Start Consult Assist</span>
          </Button>
        </div>
      )}

      {isRecording && (
        <div className="bg-white rounded-lg p-3 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`} />
              <span className="text-sm font-medium">{isPaused ? 'Paused' : 'Consult Assist listening...'}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" onClick={togglePause}>{isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}</Button>
              <Button variant="ghost" size="sm" onClick={stopRecording} className="text-red-600"><Square className="w-4 h-4" /></Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full transition-all duration-75 ${isSilent ? 'bg-gray-400' : 'bg-green-500'}`} style={{ width: `${Math.min(audioLevel * 500, 100)}%` }} />
            </div>
            {mode === 'ai-listening' && isSilent && <span className="text-xs text-gray-500">Waiting for speech...</span>}
          </div>
        </div>
      )}

      {/* Transcribing Status - Shows after recording stops */}
      {isTranscribing && !isRecording && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              <div className="absolute inset-0 w-6 h-6 border-2 border-blue-200 rounded-full animate-ping opacity-20" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">{processingStep || 'Processing audio...'}</p>
              <p className="text-xs text-blue-600 mt-0.5">Please wait while we convert your speech to text</p>
            </div>
          </div>
          <div className="mt-3 h-1.5 bg-blue-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '60%' }} />
          </div>
        </div>
      )}

      {mode === 'manual' && !rawTranscript && (
        <div className="space-y-2">
          <Textarea placeholder="Type clinical notes here..." value={editedTranscript} onChange={(e) => setEditedTranscript(e.target.value)} rows={4} className="text-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setMode('idle')}>Cancel</Button>
            <Button size="sm" onClick={() => { setRawTranscript(editedTranscript); setMode('idle'); }} disabled={!editedTranscript.trim()} className="bg-purple-600 hover:bg-purple-700">Use Text</Button>
          </div>
        </div>
      )}

      {(rawTranscript || editedTranscript) && !isRecording && (
        <div className="bg-white rounded-lg p-3 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Transcript</span>
            <Button variant="ghost" size="sm" onClick={() => setIsEditing(!isEditing)} className="text-purple-600">{isEditing ? <Check className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}</Button>
          </div>
          {isEditing ? <Textarea value={editedTranscript} onChange={(e) => setEditedTranscript(e.target.value)} rows={4} className="text-sm" /> : <p className="text-sm text-gray-600 whitespace-pre-wrap">{editedTranscript || rawTranscript}</p>}
          <div className="flex justify-between items-center mt-3">
            <Button variant="outline" size="sm" onClick={() => { setRawTranscript(''); setEditedTranscript(''); setSOAPNotes(null); setExtractedEntities(null); setDiagnosisSuggestions(null); setMode('idle'); }} className="text-gray-500">Clear All</Button>
            <Button onClick={processWithAI} disabled={isProcessing} className="bg-purple-600 hover:bg-purple-700">
              {isProcessing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{processingStep || 'Processing...'}</> : <><Sparkles className="w-4 h-4 mr-2" />Process with AI</>}
            </Button>
          </div>
        </div>
      )}

      {/* 1. ENTITIES SECTION - Same format as Voice Dictate */}
      {extractedEntities && (
        <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
          <button onClick={() => toggleSection('entities')} className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 transition-colors">
            <div className="flex items-center gap-2"><ClipboardList className="w-4 h-4 text-blue-600" /><span className="font-medium text-blue-900">Extracted Entities</span></div>
            {expandedSections.entities ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.entities && (
            <div className="p-3 space-y-2">
              {/* 1. Vitals - Red background like Voice Dictate */}
              {extractedEntities.vitals && (
                Object.values(extractedEntities.vitals).some(v => v && v !== '') && (
                  <div className="bg-red-50 rounded-lg p-2">
                    <div className="flex items-center gap-2 mb-1">
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                      <span className="text-xs font-medium text-red-800">Vitals</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      {extractedEntities.vitals.temperature && (
                        <div className="text-gray-700">Temp: {extractedEntities.vitals.temperature}°F</div>
                      )}
                      {extractedEntities.vitals.pulse && (
                        <div className="text-gray-700">Pulse: {extractedEntities.vitals.pulse}</div>
                      )}
                      {extractedEntities.vitals.respiratory_rate && (
                        <div className="text-gray-700">RR: {extractedEntities.vitals.respiratory_rate}</div>
                      )}
                      {extractedEntities.vitals.spo2 && (
                        <div className="text-gray-700">SpO2: {extractedEntities.vitals.spo2}%</div>
                      )}
                      {extractedEntities.vitals.blood_pressure && (
                        <div className="text-gray-700">BP: {extractedEntities.vitals.blood_pressure}</div>
                      )}
                      {extractedEntities.vitals.height_cm && (
                        <div className="text-gray-700">Ht: {extractedEntities.vitals.height_cm} cm</div>
                      )}
                      {extractedEntities.vitals.weight_kg && (
                        <div className="text-gray-700">Wt: {extractedEntities.vitals.weight_kg} kg</div>
                      )}
                    </div>
                  </div>
                )
              )}
              
              {/* 2. Chief Complaints - Orange background like Voice Dictate */}
              {(extractedEntities.chief_complaints_text || extractedEntities.chief_complaints?.length > 0) && (
                <div className="bg-orange-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <ClipboardList className="w-3 h-3 text-orange-600" />
                    <span className="text-xs font-medium text-orange-800">Chief Complaints</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    {extractedEntities.chief_complaints_text || extractedEntities.chief_complaints?.join(', ')}
                  </p>
                </div>
              )}
              
              {/* 3. Assessment - Teal background like Voice Dictate */}
              {extractedEntities.assessment_text && (
                <div className="bg-teal-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-3 h-3 text-teal-600" />
                    <span className="text-xs font-medium text-teal-800">Assessment</span>
                  </div>
                  <p className="text-xs text-gray-700">{extractedEntities.assessment_text}</p>
                </div>
              )}
              
              {/* 4. Tests - Yellow background like Voice Dictate */}
              {(extractedEntities.tests_text || extractedEntities.tests_mentioned?.length > 0) && (
                <div className="bg-yellow-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <TestTube className="w-3 h-3 text-yellow-600" />
                    <span className="text-xs font-medium text-yellow-800">Tests</span>
                  </div>
                  <p className="text-xs text-gray-700">
                    {extractedEntities.tests_text || extractedEntities.tests_mentioned?.join(', ')}
                  </p>
                </div>
              )}
              
              {/* 5. Diagnosis - Green background like Voice Dictate */}
              {extractedEntities.diagnosis_text && (
                <div className="bg-green-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Stethoscope className="w-3 h-3 text-green-600" />
                    <span className="text-xs font-medium text-green-800">Diagnosis</span>
                  </div>
                  <p className="text-xs text-gray-700">{extractedEntities.diagnosis_text}</p>
                </div>
              )}
              
              {/* 6. Medications - Blue background like Voice Dictate */}
              {extractedEntities.medications && extractedEntities.medications.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Pill className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-medium text-blue-800">Medications ({extractedEntities.medications.length})</span>
                  </div>
                  <div className="space-y-1">
                    {extractedEntities.medications.map((med, i) => (
                      <div key={i} className="bg-white rounded p-1.5 text-xs">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span className="font-medium text-gray-800">{med.name}</span>
                          {med.database_match ? (
                            <span className="text-green-600 text-[10px] bg-green-50 px-1 rounded">✓ DB</span>
                          ) : (
                            <span className="text-orange-500 text-[10px] bg-orange-50 px-1 rounded">Manual</span>
                          )}
                        </div>
                        {med.spoken_name && med.spoken_name.toLowerCase() !== med.name.toLowerCase().replace(/^(tab|cap|syp|inj)\s+/i, '') && (
                          <div className="text-[10px] text-gray-400 italic">
                            Voice: &quot;{med.spoken_name}&quot;
                          </div>
                        )}
                        <div className="text-gray-500">
                          {med.timing && <span>{med.timing}</span>}
                          {med.duration && <span> • {med.duration}</span>}
                          {med.time_of_day?.length > 0 && <span> • {med.time_of_day.join(', ')}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 7. Additional Notes - Gray background like Voice Dictate */}
              {extractedEntities.additional_notes && (
                <div className="bg-gray-50 rounded-lg p-2">
                  <div className="flex items-center gap-2 mb-1">
                    <StickyNote className="w-3 h-3 text-gray-600" />
                    <span className="text-xs font-medium text-gray-800">Additional Notes</span>
                  </div>
                  <p className="text-xs text-gray-700">{extractedEntities.additional_notes}</p>
                </div>
              )}
              
              {/* Action Buttons - Same as Voice Dictate */}
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setExtractedEntities(null)}
                  className="flex-1"
                >
                  <X className="w-4 h-4 mr-1" />
                  Discard
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={acceptEntities}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 2. SOAP NOTES SECTION */}
      {soapNotes && (
        <div className="bg-white rounded-lg border border-purple-200 overflow-hidden">
          <button onClick={() => toggleSection('soap')} className="w-full flex items-center justify-between p-3 bg-purple-100 hover:bg-purple-200 transition-colors">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-600" />
              <span className="font-medium text-purple-900">SOAP Notes</span>
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                {opdMode === 'minimal' ? 'Minimal' : 'Detailed'}
              </Badge>
            </div>
            {expandedSections.soap ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.soap && (
            <div className="p-3 space-y-3">
              {/* Minimal SOAP Format */}
              {opdMode === 'minimal' ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2 text-sm">
                    <div className="bg-blue-50 p-2 rounded">
                      <span className="text-xs font-medium text-blue-700 block">S</span>
                      <p className="text-gray-700 text-xs mt-1">{soapNotes.subjective_minimal || soapNotes.subjective?.split('\n')[0]?.replace('• ', '') || 'No data'}</p>
                    </div>
                    <div className="bg-green-50 p-2 rounded">
                      <span className="text-xs font-medium text-green-700 block">O</span>
                      <p className="text-gray-700 text-xs mt-1">{soapNotes.objective_minimal || soapNotes.objective?.split('\n')[0]?.replace('• ', '') || 'No data'}</p>
                    </div>
                    <div className="bg-yellow-50 p-2 rounded">
                      <span className="text-xs font-medium text-yellow-700 block">A</span>
                      <p className="text-gray-700 text-xs mt-1">{soapNotes.assessment_minimal || soapNotes.assessment?.split('\n')[0]?.replace('• ', '') || 'No data'}</p>
                    </div>
                    <div className="bg-purple-50 p-2 rounded">
                      <span className="text-xs font-medium text-purple-700 block">P</span>
                      <p className="text-gray-700 text-xs mt-1">{soapNotes.plan_minimal || soapNotes.plan?.split('\n')[0]?.replace('• ', '') || 'No data'}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => setOpdMode('detailed')} 
                    className="text-xs text-purple-600 hover:text-purple-800 underline"
                  >
                    View detailed SOAP →
                  </button>
                </div>
              ) : (
                /* Detailed SOAP Format */
                <>
                  <div>
                    <span className="text-sm text-purple-700 font-medium">S - Subjective</span>
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-1 whitespace-pre-line">
                      {soapNotes.subjective || 'No data'}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-purple-700 font-medium">O - Objective</span>
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-1 whitespace-pre-line">
                      {soapNotes.objective || 'No data'}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-purple-700 font-medium">A - Assessment</span>
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-1 whitespace-pre-line">
                      {soapNotes.assessment || 'No data'}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-purple-700 font-medium">P - Plan</span>
                    <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded mt-1 whitespace-pre-line">
                      {soapNotes.plan || 'No data'}
                    </div>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="text-gray-500"><X className="w-4 h-4 mr-1" />Reject</Button>
                <Button size="sm" onClick={acceptSOAPNotes} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" />Apply SOAP & Save</Button>
              </div>
              <p className="text-xs text-purple-600 mt-2 text-center">⚠️ Click &quot;Apply SOAP &amp; Save&quot; to enable SOAP button on session card</p>
            </div>
          )}
        </div>
      )}

      {/* 3. DIAGNOSIS SECTION - Only Primary + Differential, NO red flags */}
      {diagnosisSuggestions && (
        <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
          <button onClick={() => toggleSection('diagnosis')} className="w-full flex items-center justify-between p-3 bg-green-100 hover:bg-green-200 transition-colors">
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-green-600" />
              <span className="font-medium text-green-900">Diagnosis</span>
              <Badge variant="outline" className="text-xs bg-yellow-100 text-yellow-700 border-yellow-300">Non-decisional</Badge>
            </div>
            {expandedSections.diagnosis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {expandedSections.diagnosis && (
            <div className="p-3 space-y-3">
              {/* Primary Diagnosis - Single line for minimal, detailed for detailed mode */}
              {diagnosisSuggestions.primary_diagnosis && (
                opdMode === 'minimal' ? (
                  <div className="bg-green-50 p-2 rounded flex items-center gap-2">
                    <span className="font-medium text-green-800">Primary:</span>
                    <span className="text-green-900">{diagnosisSuggestions.primary_diagnosis.name}</span>
                    <Badge className={`text-xs ${diagnosisSuggestions.primary_diagnosis.confidence === 'high' ? 'bg-green-500' : diagnosisSuggestions.primary_diagnosis.confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-500'}`}>
                      {diagnosisSuggestions.primary_diagnosis.confidence}
                    </Badge>
                  </div>
                ) : (
                  <div className="bg-green-50 p-2 rounded">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-green-800">Primary:</span>
                      <span className="text-green-900">{diagnosisSuggestions.primary_diagnosis.name}</span>
                      <Badge className={`text-xs ${diagnosisSuggestions.primary_diagnosis.confidence === 'high' ? 'bg-green-500' : diagnosisSuggestions.primary_diagnosis.confidence === 'medium' ? 'bg-yellow-500' : 'bg-gray-500'}`}>
                        {diagnosisSuggestions.primary_diagnosis.confidence}
                      </Badge>
                    </div>
                    {diagnosisSuggestions.primary_diagnosis.reasoning && (
                      <p className="text-xs text-gray-600">{diagnosisSuggestions.primary_diagnosis.reasoning}</p>
                    )}
                  </div>
                )
              )}
              
              {/* Differential Diagnoses - Always show (mandatory for detailed mode) */}
              {diagnosisSuggestions.differential_diagnoses?.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500">Differential Diagnoses:</span>
                  <div className="space-y-1 mt-1">
                    {diagnosisSuggestions.differential_diagnoses.map((d, i) => (
                      <div key={i} className="text-sm flex items-center gap-2">
                        <span className="text-gray-400">•</span>
                        <span>{d.name}</span>
                        <Badge variant="outline" className="text-xs">{d.confidence}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* NO red_flags section - removed as per requirement */}
              
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" className="text-gray-500"><X className="w-4 h-4 mr-1" />Reject</Button>
                <Button size="sm" onClick={acceptDiagnosis} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" />Apply Diagnosis</Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-100 p-2 rounded">
        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <div><strong>Compliance:</strong> AI suggestions are non-decisional. Doctor approval required. All interactions logged. No audio stored.</div>
      </div>
    </div>
  );
};

export default AIClinicalAssistant;
